import React, { useState, useEffect, useCallback } from 'react';
import { X, Upload, FileSpreadsheet } from 'lucide-react';
import { getAllClients, uploadClientTripSheet, uploadGlobalTripSheet } from '../../api/createClientApi';
import CustomSelect from '../shared/CustomSelect';

const UploadSheetWizard = ({ isOpen, onClose, onSave }) => {
  const [clientId, setClientId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [clients, setClients] = useState([]);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const response = await getAllClients(1, 100);
      const clientsData = response.data || response.clients || response || [];
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (err) {
      setError('Failed to load clients');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError('');
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
    }
  }, []);

  const handleUpload = async () => {
    if (!clientId) {
      setError('Please select a client or select Global Multi-Client upload');
      return;
    }
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      let response;
      if (clientId === 'global') {
        response = await uploadGlobalTripSheet(file, fromDate, toDate);
      } else {
        response = await uploadClientTripSheet(file, clientId, fromDate, toDate);
      }
      onSave?.(response);
      onClose();
      setFile(null);
      setClientId('');
      setFromDate('');
      setToDate('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload trip sheet');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setClientId('');
    setFromDate('');
    setToDate('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div onClick={handleClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        backgroundColor: 'white', zIndex: 2001, width: '90%', maxWidth: '1000px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)', maxHeight: '90vh', display: 'flex', flexDirection: 'column'
      }}>
        
        {/* Header */}
        <div style={{ backgroundColor: '#227B48', padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: 'white' }}>Upload Roaster</h2>
          <button onClick={handleClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#F04438',
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px', fontWeight: 500
          }}>
            Cancel <X size={20} style={{ backgroundColor: '#F04438', color: 'white', borderRadius: '50%', padding: '2px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '32px', overflowY: 'auto' }}>
          
          {error && (
            <div style={{ 
              backgroundColor: '#FEE2E2', 
              color: '#DC2626', 
              padding: '12px 16px', 
              borderRadius: '4px', 
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Form Fields: Client + From Date + To Date */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '32px', flexWrap: 'wrap' }}>
            
            {/* Client Selector */}
            <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', flex: '1 1 240px' }}>
              <label style={{
                position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
                padding: '0 6px', fontSize: '11px', color: '#6B7280', fontWeight: 500, zIndex: 10,
                borderRadius: '2px', pointerEvents: 'none'
              }}>
                Client
              </label>
              <CustomSelect
                value={clientId}
                onChange={(val) => setClientId(val)}
                disabled={isLoading}
                options={[
                  { value: 'global', label: 'Global Multi-Client File Upload (Multi-Tab)' },
                  ...clients.map((c) => ({
                    value: c.id || c.clientId,
                    label: c.clientName || c.name || c.client_name,
                  }))
                ]}
                placeholder={isLoading ? 'Loading...' : 'Select Client'}
                searchable
                width="100%"
              />
            </div>

            {/* From Date */}
            <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', flex: '1 1 180px' }}>
              <label style={{
                position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
                padding: '0 6px', fontSize: '11px', color: '#6B7280', fontWeight: 500, zIndex: 10,
                borderRadius: '2px', pointerEvents: 'none'
              }}>
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px',
                  fontSize: '14px', color: '#333', outline: 'none', backgroundColor: 'white',
                  width: '100%', boxSizing: 'border-box', height: '42px'
                }}
              />
            </div>

            {/* To Date */}
            <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', flex: '1 1 180px' }}>
              <label style={{
                position: 'absolute', top: '-8px', left: '12px', backgroundColor: 'white',
                padding: '0 6px', fontSize: '11px', color: '#6B7280', fontWeight: 500, zIndex: 10,
                borderRadius: '2px', pointerEvents: 'none'
              }}>
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px',
                  fontSize: '14px', color: '#333', outline: 'none', backgroundColor: 'white',
                  width: '100%', boxSizing: 'border-box', height: '42px'
                }}
              />
            </div>

          </div>

          {/* Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
            style={{
              border: `2px dashed ${isDragging ? '#227B48' : '#BDBDBD'}`,
              borderRadius: '8px',
              padding: '24px',
              textAlign: 'center',
              margin: '0 16px',
              cursor: 'pointer',
              backgroundColor: isDragging ? '#F0FDF4' : 'transparent',
              transition: 'all 0.2s ease',
              minHeight: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {file ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', width: '100%' }}>
                <FileSpreadsheet size={32} color="#227B48" />
                <div style={{ textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>{file.name}</p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Upload size={48} color="#666" style={{ marginBottom: '16px' }} />
                <p style={{ fontSize: '20px', color: '#666', fontWeight: 600, margin: 0 }}>
                  Drop your file here or click to browse
                </p>
                <p style={{ fontSize: '14px', color: '#999', margin: '8px 0 0 0' }}>
                  Supports .xlsx, .xls, .csv files
                </p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px', gap: '12px' }}>
            <button
              onClick={handleClose}
              style={{
                padding: '12px 24px',
                border: '1px solid #C4C4C4',
                borderRadius: '4px',
                backgroundColor: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={isUploading || !file || !clientId}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: isUploading || !file || !clientId ? '#C4C4C4' : '#227B48',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                cursor: isUploading || !file || !clientId ? 'not-allowed' : 'pointer'
              }}
            >
              {isUploading ? 'Uploading...' : 'Upload Trip Sheet'}
            </button>
          </div>

        </div>

      </div>
    </>
  );
};

export default UploadSheetWizard;

